import React, {useEffect} from 'react'
import {useIntl} from 'react-intl'
import {
    Box,
    Button,
    Text,
    Popover,
    PopoverContent,
    PopoverBody,
    PopoverTrigger,
    useDisclosure,
    VisuallyHidden,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LocaleText from '../locale-text'
import {CheckIcon, ChevronDownIcon} from '../custom-icons'
import {getPathWithLocale} from '@app/utils/url'
import {buildUrlWithLocale} from '@app/utils/url'

import useMultiSite from '../../hooks/use-multi-site'
import {useLocation} from 'react-router-dom'

/**
 * DrawerMenuLocaleSelector is a component that provides a dropdown menu
 * for selecting the locale within a drawer menu. It utilizes Chakra UI's
 * Menu components to display a list of supported locales, allowing users
 * to switch the language of the application.
 *
 */
const DrawerMenuLocaleSelector = () => {
    const intl = useIntl()
    const location = useLocation()
    const {site, locale: currentLocale, buildUrl} = useMultiSite()
    const {l10n} = site
    const supportedLocales = l10n?.supportedLocales
    const {onOpen, onClose, isOpen} = useDisclosure()
    const menuBtnRef = React.useRef(null)
    const firstLinkRef = React.useRef(null)
    const popoverContentRef = React.useRef(null)

    // Align locale selector accessibility to keep "popover popup" behavior only without specific aria-*
    useEffect(() => {
        if (menuBtnRef.current) {
            menuBtnRef.current.removeAttribute('aria-haspopup')
        }
        if (popoverContentRef.current) {
            popoverContentRef.current.removeAttribute('role')
        }
    }, [])

    return (
        <Popover
            placement="top-end"
            variant="navLocaleMenu"
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            initialFocusRef={firstLinkRef}
        >
            <PopoverTrigger>
                <Button
                    ref={menuBtnRef}
                    textStyle={'bodyBase2'}
                    textTransform={'uppercase'}
                    whiteSpace={'normal'}
                    textDecoration={'none'}
                    lineHeight={'1.1'}
                    p="16px"
                    variant={'link'}
                >
                    <Text as="span">
                        <LocaleText shortCode={intl.locale} />
                    </Text>
                    <ChevronDownIcon boxSize={3} ml={4} aria-hidden="true" />
                </Button>
            </PopoverTrigger>
            <PopoverContent ref={popoverContentRef}>
                <PopoverBody>
                    <VisuallyHidden>
                        <Text>
                            {intl.formatMessage({
                                defaultMessage: 'Language selector',
                                id: 'locale_selector.title'
                            })}
                        </Text>
                    </VisuallyHidden>
                    <Box defaultValue={intl.locale} as="ul">
                        {supportedLocales.map((localeObject, index) => {
                            const locale = localeObject.id
                            const localeAlias = localeObject.alias
                            const isFirst = index === 0

                            return (
                                <Box
                                    key={localeAlias}
                                    as="li"
                                    textAlign="center"
                                    listStyleType="none"
                                >
                                    <Link
                                        ref={isFirst ? firstLinkRef : undefined}
                                        translate="no"
                                        href={buildUrlWithLocale(
                                            location,
                                            site,
                                            currentLocale,
                                            localeObject
                                        )}
                                        onClick={() => {
                                            const newUrl = getPathWithLocale(locale, buildUrl, {
                                                disallowParams: ['refine']
                                            })
                                            window.location = newUrl
                                        }}
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        textDecoration="none"
                                        textTransform="uppercase"
                                        position="relative"
                                        width="auto"
                                        height="50px"
                                        px="32px"
                                    >
                                        <Text lang={locale}>
                                            <LocaleText shortCode={locale} />
                                        </Text>

                                        {intl.locale === locale && (
                                            <>
                                                <VisuallyHidden>
                                                    {intl.formatMessage({
                                                        defaultMessage: 'Selected',
                                                        id: 'drawer_menu.locale_selector.selected_language'
                                                    })}
                                                </VisuallyHidden>
                                                <CheckIcon
                                                    boxSize={4}
                                                    position="absolute"
                                                    top="50%"
                                                    transform="translateY(-50%)"
                                                    right="10px"
                                                    aria-hidden="true"
                                                />
                                            </>
                                        )}
                                    </Link>
                                </Box>
                            )
                        })}
                    </Box>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

export default DrawerMenuLocaleSelector
